/**
 * One-ATC purchase options (Mixer + Variant Mix + Quantity + Single).
 * Cart payload: ordinary { id, quantity } lines — no operational _bundle_* properties.
 * ATC verify/rollback: quantity-vector by variant (not _bundle_group_id).
 * Gifts: BOGOS is the sole writer — theme never submits gift lines.
 * Pricing: PDP cards show standalone pack benefits from offer config (0/5/10/15).
 * Variant mix: pack sale/compare from allocated tray variant prices × tier %.
 * Cart Transform remains authoritative at cart/checkout for the cart-wide ladder.
 * Gifts: when giftCatalog + giftTiers exist on campaign_pricing, cards show the
 * gifts for that pack size only (display-only). Full static gift UVP feeds
 * Gesamtwert and Du sparst.
 * Cart state is read only for eligible-unit counting and the context note.
 * Code discounts are never included in PDP card/CTA prices.
 * PDP flavor sync: FoxTheme variantChange → Single + pack imagery/price; allocator qty preserved.
 *
 * Limitation: without a correlation property, a concurrent same-variant add from another
 * tab cannot always be distinguished from this request. Overshoot is protected and
 * pre-ATC quantities are floored; perfect attribution is impossible by design.
 *
 * Gift UVP helpers: assets/ppo-gift-value.js (loaded first).
 */

class PdpPurchaseOptions extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;

    this.debugEnabled = new URLSearchParams(window.location.search).has('bundle_debug');
    this.flavorCounts = new Map();
    /** True after the shopper edits a variant_mix allocation; blocks silent reseeds on top-flavour change. */
    this.mixAllocationTouched = false;
    this.isSubmitting = false;
    this.currentVariant = null;
    this.variantsById = new Map();
    this.variantAvailability = new Map();
    /** Flavor variantId → units already in cart (lines + item_components). */
    this.cartReservations = new Map();
    /** Cart-wide eligible units for the context note only (not card pricing). */
    this.eligibleCartUnits = 0;
    this.campaignConfig = null;
    /** @type {Map<string, { key: string, title: string, price: number, image: string, resolved: boolean }>} */
    this.campaignGiftByKey = new Map();
    this._unsubscribers = [];

    this.offers = Array.from(this.querySelectorAll('[data-offer]'));
    this.cta = this.querySelector('[data-ppo-cta]');
    this.ctaLabel = this.querySelector('[data-ppo-cta-label]');
    this.ctaPrice = this.querySelector('[data-ppo-cta-price]');
    this.hintEl = this.querySelector('[data-ppo-hint]');
    this.cartContextEl = this.querySelector('[data-ppo-cart-context]');
    this.cartContextTemplate = this.dataset.cartContextTemplate || '';
    this.errorEl = this.querySelector('[data-ppo-error]');
    this.sharedMixer = this.querySelector('[data-shared-mixer]');
    this.mixerStatus = this.sharedMixer?.querySelector('[data-mixer-status]');
    this.summarySale = this.querySelector('[data-ppo-summary-sale]');
    this.summaryCompare = this.querySelector('[data-ppo-summary-compare]');
    this.summaryChip = this.querySelector('[data-ppo-summary-chip]');
    this.summaryUnit = this.querySelector('[data-ppo-summary-unit]');
    this.unitPrice = Number(this.dataset.unitPrice || 0);
    this.moneyFormat =
      this.dataset.moneyFormat || window.FoxTheme?.settings?.moneyFormat || '{{amount}}';
    this.currencyCode = this.dataset.currencyCode || window.Shopify?.currency?.active || '';
    this.soldOutLabel = this.dataset.soldOutLabel || 'Ausverkauft';
    this.remainingLabelTemplate = this.dataset.remainingLabel || 'Nur noch {n} verfügbar';
    this.inventoryUnavailableLabel =
      this.dataset.inventoryUnavailableLabel ||
      'Die gewählte Menge ist nicht mehr verfügbar. Bitte passe dein Bundle an.';
    this.giftGratisWord = this.dataset.giftGratis || 'Gratis';
    this.giftCardLabels = {
      mixer: this.dataset.giftLabelMixer || 'Mixer',
      cup: this.dataset.giftLabelCup || '1,2-L-Drink-Cup',
    };
    this.forceUnavailable = this.dataset.forceUnavailable === 'true';
    this.defaultCtaLabel = this.dataset.ctaLabel || 'In den Warenkorb';
    this.productFallbackImage = this.dataset.productImage || '';

    this.readVariantCatalog();
    this.campaignConfig = this.readCampaignConfig();
    this.loadCampaignResolvedGifts();
    this.bindOfferEvents();
    this.bindMixerEvents();
    this.bindVariantSync();
    this.bindCartSync();

    this.cta?.addEventListener('click', () => this.handleAddToCart());

    const initial = this.offers.find((o) => o.querySelector('input[type="radio"]')?.checked) || this.offers[0];
    if (initial) this.selectOffer(initial, { resetMix: true });

    const bootstrap = this.resolveBootstrapVariant();
    if (bootstrap) this.handleVariantChange(bootstrap, { source: 'bootstrap' });
    else if (this.currentVariant) this.syncSummaryMeta(this.currentVariant);

    // Only when this element mounts (offers UI actually rendered) — not on gated/fail-closed notes.
    this.suppressNativeBuyBox();
    this.setAttribute('data-ppo-ready', 'true');
    this.removeAttribute('data-ppo-init-failed');
    document.documentElement.removeAttribute('data-ppo-ssr-owns-atc');
    if (this.debugEnabled) document.documentElement.classList.add('ppo-debug');

    // Authoritative remaining stock = Liquid raw − cart reservations (Phase T).
    this.refreshCartReservations().catch((error) => this.log('cart hydrate failed', error));
  }

  disconnectedCallback() {
    if (this.debugEnabled) document.documentElement.classList.remove('ppo-debug');
    this.removeAttribute('data-ppo-ready');
    this._unsubscribers.forEach((unsub) => {
      try {
        unsub();
      } catch (_) {
        /* ignore */
      }
    });
    this._unsubscribers = [];
    this.restoreNativeBuyBox();
  }

  suppressNativeBuyBox() {
    document.documentElement.classList.add('ppo-owns-atc');
    this.suppressNativeBlocks([
      '.product__block--price',
      '.product__block--buy_buttons',
      '.product-form__quantity',
      'quantity-input.quantity',
    ]);
    this.suppressNativeStickyAtc();
  }

  restoreNativeBuyBox() {
    this.restoreNativeBlocks();
    this.restoreNativeStickyAtc();
    document.documentElement.classList.remove('ppo-owns-atc');
    document.documentElement.removeAttribute('data-ppo-ssr-owns-atc');
  }

  getProductSectionRoot() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return document;
    return (
      document.getElementById(`shopify-section-${sectionId}`) ||
      document.querySelector(`[data-section-id="${sectionId}"]`) ||
      document
    );
  }

  suppressNativeBlocks(selectors) {
    const root = this.getProductSectionRoot();
    const marked = [];
    selectors.forEach((selector) => {
      root.querySelectorAll(selector).forEach((el) => {
        if (el.closest('pdp-purchase-options')) return;
        if (el.getAttribute('data-ppo-suppressed') === 'true') return;
        el.setAttribute('data-ppo-suppressed', 'true');
        el.setAttribute('aria-hidden', 'true');
        el.hidden = true;
        marked.push(el);
      });
    });
    this._suppressedNativeBlocks = marked;
  }

  restoreNativeBlocks() {
    const marked =
      this._suppressedNativeBlocks ||
      Array.from(document.querySelectorAll('[data-ppo-suppressed="true"]')).filter(
        (el) => el.id !== 'shopify-section-sticky-atc-bar' && !el.closest('#shopify-section-sticky-atc-bar')
      );
    marked.forEach((el) => {
      if (!el || el.getAttribute('data-ppo-suppressed') !== 'true') return;
      if (el.id === 'shopify-section-sticky-atc-bar') return;
      el.hidden = false;
      el.removeAttribute('data-ppo-suppressed');
      el.removeAttribute('aria-hidden');
    });
    this._suppressedNativeBlocks = [];
  }

  bindOfferEvents() {
    this.offers.forEach((offer) => {
      offer.addEventListener('change', (event) => {
        if (event.target.matches('input[type="radio"]')) {
          this.selectOffer(offer, { resetMix: true });
        }
      });

      offer.addEventListener('click', (event) => {
        if (event.target.closest('[data-qty-minus], [data-qty-plus], [data-shared-mixer]')) return;
        const input = offer.querySelector('input[type="radio"]');
        if (input && !input.checked) {
          input.checked = true;
          this.selectOffer(offer, { resetMix: true });
        }
      });
    });
  }

  bindMixerEvents() {
    if (!this.sharedMixer) return;

    this.sharedMixer.querySelectorAll('[data-variant-id]').forEach((row) => {
      const id = row.dataset.variantId;
      if (id && !this.flavorCounts.has(id)) this.flavorCounts.set(id, 0);
    });

    this.sharedMixer.querySelectorAll('[data-qty-minus]').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const row = btn.closest('[data-variant-id]');
        this.adjustQty(row?.dataset.variantId, -1);
      });
    });

    this.sharedMixer.querySelectorAll('[data-qty-plus]').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const row = btn.closest('[data-variant-id]');
        this.adjustQty(row?.dataset.variantId, 1);
      });
    });
  }

  mountSharedMixer(offer) {
    if (!this.sharedMixer) return;
    const slot = offer?.querySelector('[data-mixer-slot]');
    if (this.usesVariantAllocator(offer) && slot) {
      slot.appendChild(this.sharedMixer);
      this.sharedMixer.hidden = false;
      return;
    }
    this.sharedMixer.hidden = true;
    if (this.sharedMixer.parentElement !== this) {
      this.appendChild(this.sharedMixer);
    }
  }

  bindVariantSync() {
    const pubsub = window.FoxTheme?.pubsub;
    if (pubsub?.subscribe && pubsub.PUB_SUB_EVENTS?.variantChange) {
      const unsub = pubsub.subscribe(pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
        if (event?.data?.sectionId !== this.dataset.sectionId) return;
        this.handleVariantChange(event.data.variant, { source: 'pubsub' });
      });
      this._unsubscribers.push(unsub);
    }

    // Narrow fallback only: main product form id (never the ppo fallback form).
    this._onIdFallback = (event) => {
      const target = event.target;
      if (!target?.matches?.('input[name="id"], select[name="id"]')) return;
      if (target.hasAttribute('data-ppo-fallback-id')) return;
      if (target.closest('form.ppo__product-form')) return;
      if (target.closest('#shopify-section-sticky-atc-bar')) return;

      const sectionId = this.dataset.sectionId;
      const sectionRoot = sectionId
        ? document.getElementById(`glp-${sectionId}`) ||
          document.getElementById(`shopify-section-${sectionId}`)
        : null;
      const nativeRoot = target.closest('[data-glp-native-purchase]');
      if (nativeRoot) {
        if (sectionRoot && !sectionRoot.contains(nativeRoot)) return;
        // Beauty Collagen Bar: native owns Single; PPO catalog is tray-only.
        if (sectionRoot?.getAttribute('data-bar-pack') === 'single') return;
      } else {
        const form = target.closest('form');
        if (
          sectionId &&
          form?.id &&
          form.id !== `product-form-${sectionId}` &&
          !form.id.includes(sectionId)
        ) {
          return;
        }
      }

      const id = Number(target.value);
      if (!id || id === this.currentVariant?.id) return;
      const fromCatalog = this.variantsById.get(String(id));
      if (fromCatalog) this.handleVariantChange(fromCatalog, { source: 'id-fallback' });
    };
    document.addEventListener('change', this._onIdFallback);
    document.addEventListener('input', this._onIdFallback);
    this._unsubscribers.push(() => {
      document.removeEventListener('change', this._onIdFallback);
      document.removeEventListener('input', this._onIdFallback);
    });
  }

  bindCartSync() {
    const pubsub = window.FoxTheme?.pubsub;
    if (pubsub?.subscribe && pubsub.PUB_SUB_EVENTS?.cartUpdate) {
      const unsub = pubsub.subscribe(pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (this._publishingCart) return;
        const cart = event?.cart;
        if (cart?.items) {
          this.onExternalCartChange(cart);
        } else {
          this.refreshCartReservations().catch((error) => this.log('cartUpdate refresh failed', error));
        }
      });
      this._unsubscribers.push(unsub);
    }

    this._onCartUpdated = (event) => {
      if (this._publishingCart) return;
      const cart = event?.detail?.cart;
      if (cart?.items) {
        this.onExternalCartChange(cart);
      } else {
        this.refreshCartReservations().catch((error) => this.log('cart:updated refresh failed', error));
      }
    };
    document.addEventListener('cart:updated', this._onCartUpdated);
    this._unsubscribers.push(() => document.removeEventListener('cart:updated', this._onCartUpdated));
  }

  onExternalCartChange(cart) {
    this.applyCartReservations(cart);
    this.applyCampaignCartState(cart);
    this.pruneIneligibleMixSelections();
    this.syncFlavorUi();
    this.syncIncludedLines();
    this.syncOfferPrices();
    this.updateUI();
  }

  /**
   * Count Beauty Drink flavor units already in the cart.
   * - Prefer Ajax item_components when present.
   * - Merged carrier lines: use Function-owned `_bundle_units` (not a flavor variant).
   * - Unmerged flavor lines: count variant_id × quantity only (ignore any client _bundle_units).
   */
  buildCartReservations(cart) {
    const reserved = new Map();
    const add = (variantId, quantity) => {
      if (!this.isFlavorVariantId(variantId)) return;
      const qty = Number(quantity || 0);
      if (qty <= 0) return;
      const id = String(variantId);
      reserved.set(id, (reserved.get(id) || 0) + qty);
    };

    for (const item of cart?.items || []) {
      const lineQty = Number(item.quantity || 0);
      if (lineQty <= 0) continue;

      const components = item.item_components;
      if (Array.isArray(components) && components.length > 0) {
        components.forEach((component) => add(component.variant_id, component.quantity));
        continue;
      }

      // Unmerged component/single flavor line — never multiply via _bundle_units JSON.
      if (this.isFlavorVariantId(item.variant_id)) {
        add(item.variant_id, lineQty);
        continue;
      }

      // Merged carrier (or other non-flavor line): Function writes one _bundle_units.
      const unitsRaw = item.properties?._bundle_units;
      if (unitsRaw) {
        try {
          const units = typeof unitsRaw === 'string' ? JSON.parse(unitsRaw) : unitsRaw;
          if (units && typeof units === 'object') {
            Object.entries(units).forEach(([variantId, quantity]) => {
              add(variantId, Number(quantity) * lineQty);
            });
          }
        } catch (error) {
          this.log('_bundle_units parse failed', error);
        }
      }
    }
    return reserved;
  }

  applyCartReservations(cart) {
    this.cartReservations = this.buildCartReservations(cart);
    this.log('cartReservations', Object.fromEntries(this.cartReservations));
  }

  readCampaignConfig() {
    const script =
      this.querySelector('[data-ppo-campaign-config]') ||
      this.parentElement?.querySelector('[data-ppo-campaign-config]');
    if (!script) return null;
    try {
      const parsed = JSON.parse(script.textContent || 'null');
      if (!parsed || typeof parsed !== 'object') return null;
      const forcePreview =
        this.closest('[data-ppo-force-preview="true"]') != null ||
        this.getAttribute('data-ppo-force-preview') === 'true' ||
        this.dataset.ppoForcePreview === 'true';
      // Live: only enabled:true. Dev force_preview: may consume enabled:false mirror.
      const allowDisabledPreview = forcePreview && parsed.enabled === false;
      if (parsed.enabled !== true && !allowDisabledPreview) return null;
      if (!Array.isArray(parsed.eligibleProductIds) || parsed.eligibleProductIds.length === 0) {
        return null;
      }
      if (!Array.isArray(parsed.aggregateTiers) || parsed.aggregateTiers.length === 0) {
        return null;
      }
      const giftCatalog =
        parsed.giftCatalog && typeof parsed.giftCatalog === 'object' && !Array.isArray(parsed.giftCatalog)
          ? parsed.giftCatalog
          : null;
      const giftTiers = Array.isArray(parsed.giftTiers) ? parsed.giftTiers : null;
      return {
        schemaVersion: Number(parsed.schemaVersion) || 2,
        // Projection treats preview mirror as display-enabled without flipping shop enable.
        enabled: true,
        previewMirror: allowDisabledPreview,
        eligibleProductIds: parsed.eligibleProductIds.map(String),
        aggregateTiers: parsed.aggregateTiers,
        giftCatalog,
        giftTiers,
      };
    } catch (error) {
      this.log('campaign config parse failed', error);
      return null;
    }
  }

  loadCampaignResolvedGifts() {
    this.campaignGiftByKey = new Map();
    const script =
      this.querySelector('[data-ppo-campaign-gifts]') ||
      this.parentElement?.querySelector('[data-ppo-campaign-gifts]');
    if (!script) return;
    try {
      const parsed = JSON.parse(script.textContent || 'null');
      if (!Array.isArray(parsed)) return;
      for (const entry of parsed) {
        if (!entry || typeof entry.key !== 'string' || !entry.key) continue;
        this.campaignGiftByKey.set(entry.key, {
          key: entry.key,
          title: String(entry.title || ''),
          price: Number(entry.price || 0),
          image: String(entry.image || ''),
          resolved: entry.resolved === true,
        });
      }
    } catch (error) {
      this.log('campaign gifts parse failed', error);
    }
  }

  hasCampaignGifts() {
    if ((this.dataset.suppressCampaignGifts || '').trim() === 'true') return false;
    const tiers = this.campaignConfig?.giftTiers;
    return Array.isArray(tiers) && tiers.length > 0 && this.campaignGiftByKey.size > 0;
  }

  /**
   * Highest matching gift tier keys (same minQuantity wins semantics as CT %).
   */
  resolveGiftKeysAtUnits(units) {
    const tiers = this.campaignConfig?.giftTiers || [];
    const n = Math.max(0, Number(units) || 0);
    let bestMin = -1;
    let keys = [];
    for (const tier of tiers) {
      const minQty = Number(tier.minimumQuantity);
      if (!Number.isFinite(minQty)) continue;
      if (n >= minQty && minQty >= bestMin) {
        bestMin = minQty;
        keys = Array.isArray(tier.giftKeys) ? tier.giftKeys.filter((k) => typeof k === 'string' && k) : [];
      }
    }
    return keys;
  }

  resolveGiftItemsAtUnits(units) {
    if (!this.hasCampaignGifts()) return [];
    return this.resolveGiftKeysAtUnits(units)
      .map((key) => this.campaignGiftByKey.get(key))
      .filter((gift) => gift && gift.resolved === true);
  }

  giftCardLabel(gift) {
    return ppoGiftCardLabel(gift?.key, this.giftCardLabels, gift?.title);
  }

  giftBenefitLabel(gift) {
    return ppoGiftBenefitLabel(
      gift?.key,
      this.giftCardLabels,
      this.giftGratisWord,
      gift?.title,
    );
  }

  formatGiftSummary(items) {
    if (!items?.length) return '';
    return items.map((gift) => this.giftBenefitLabel(gift)).filter(Boolean).join(' · ');
  }

  buildGiftBenefitRowHtml(gift) {
    const title = this.escapeHtml(this.giftBenefitLabel(gift));
    return `<span class="ppo__benefit"><svg class="ppo__icon" width="12" height="12" aria-hidden="true" focusable="false"><use href="#ppo-icon-gift"></use></svg><span class="ppo__benefit-label">${title}</span></span>`;
  }

  buildGiftBenefitHtml(items) {
    return (items || []).map((gift) => this.buildGiftBenefitRowHtml(gift)).join('');
  }

  buildCampaignGiftRowHtml(gift) {
    const title = this.escapeHtml(this.giftCardLabel(gift));
    const img = gift.image
      ? `<img src="${this.escapeHtml(gift.image)}" alt="" width="26" height="26" loading="lazy" decoding="async">`
      : '';
    const price =
      Number(gift.price || 0) > 0 ? `<s>${this.escapeHtml(this.formatMoney(gift.price))}</s>` : '';
    return `<span class="ppo__inc-row" data-campaign-gift-row data-campaign-gift-key="${this.escapeHtml(gift.key)}"><span class="ppo__inc-icon" aria-hidden="true"><svg class="ppo__icon" width="12" height="12" aria-hidden="true" focusable="false"><use href="#ppo-icon-gift"></use></svg></span><span class="ppo__inc-thumb" aria-hidden="true">${img}</span><span class="ppo__inc-lbl">${title}</span><span class="ppo__inc-val">${price}<span class="ppo__inc-free">${this.escapeHtml(this.giftGratisWord)}</span></span></span>`;
  }

  syncOfferGifts(offer) {
    if (!offer || !this.hasCampaignGifts()) return;

    const isSingle = offer.dataset.offerKind === 'single';
    const pack = isSingle ? 1 : Math.max(0, Number(offer.dataset.packSize || 1));
    const displayItems = this.resolveGiftItemsAtUnits(pack);

    offer._displayGiftItems = displayItems;

    const summary = this.formatGiftSummary(displayItems);
    offer.dataset.giftSummary = summary;

    const body = offer.querySelector('.ppo__offer-body');
    let benefit = offer.querySelector('[data-offer-gift-benefit]');
    if (displayItems.length) {
      if (!benefit && body) {
        benefit = document.createElement('span');
        benefit.className = 'ppo__gift-benefits';
        benefit.setAttribute('data-offer-gift-benefit', '');
        const sub = body.querySelector('.ppo__offer-sub');
        if (sub?.nextSibling) body.insertBefore(benefit, sub.nextSibling);
        else if (sub) sub.after(benefit);
        else body.appendChild(benefit);
      }
      if (benefit) {
        benefit.hidden = false;
        benefit.classList.add('ppo__gift-benefits');
        benefit.innerHTML = this.buildGiftBenefitHtml(displayItems);
      }
    } else if (benefit) {
      benefit.hidden = true;
      benefit.innerHTML = '';
    }

    const included = offer.querySelector('.ppo__included');
    if (included) {
      included.querySelectorAll('[data-campaign-gift-row]').forEach((row) => row.remove());
      const linesHost = included.querySelector('[data-included-lines]');
      let insertBefore = null;
      if (linesHost) {
        insertBefore = linesHost.nextElementSibling;
        while (
          insertBefore &&
          (insertBefore.hasAttribute('data-campaign-gift-row') ||
            insertBefore.hasAttribute('data-bonus-free-row'))
        ) {
          insertBefore = insertBefore.nextElementSibling;
        }
      }
      for (const gift of displayItems) {
        const wrapper = document.createElement('template');
        wrapper.innerHTML = this.buildCampaignGiftRowHtml(gift).trim();
        const row = wrapper.content.firstElementChild;
        if (!row) continue;
        if (insertBefore) included.insertBefore(row, insertBefore);
        else included.appendChild(row);
      }
    }

    const hasPricedGift = displayItems.some((g) => Number(g.price || 0) > 0);
    if (hasPricedGift) offer.dataset.valueConfigured = 'true';
  }

  syncAllOfferGifts() {
    if (!this.hasCampaignGifts()) return;
    this.offers.forEach((offer) => {
      this.syncOfferGifts(offer);
      this.syncValueFooter(offer, this.offerMetrics(offer));
    });
  }

  normalizeProductId(id) {
    const raw = String(id || '');
    const match = raw.match(/Product\/(\d+)/i);
    if (match) return match[1];
    if (/^\d+$/.test(raw)) return raw;
    return raw;
  }

  applyCampaignCartState(cart) {
    this.eligibleCartUnits = this.countEligibleCartUnits(cart);
    this.log('campaignCart', {
      eligibleCartUnits: this.eligibleCartUnits,
    });
  }

  countEligibleCartUnits(cart) {
    if (!this.campaignConfig?.enabled) return 0;
    const eligible = new Set(
      this.campaignConfig.eligibleProductIds.map((id) => this.normalizeProductId(id))
    );
    let total = 0;
    for (const item of cart?.items || []) {
      const qty = Number(item.quantity || 0);
      if (qty <= 0) continue;
      const productId = this.normalizeProductId(item.product_id);
      if (!eligible.has(productId)) continue;
      total += qty;
    }
    return total;
  }

  countVariantQuantityInCart(cart, variantId) {
    return this.buildCartReservations(cart).get(String(variantId)) || 0;
  }

  async refreshCartReservations() {
    const cart = await this.fetchCart();
    this.applyCartReservations(cart);
    this.applyCampaignCartState(cart);
    this.pruneIneligibleMixSelections();
    this.syncFlavorUi();
    this.syncIncludedLines();
    this.syncOfferPrices();
    this.updateUI();
    return cart;
  }

  collectCartSections() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
    );
    const max = window.FoxTheme?.cartUi?.MAX_SECTIONS || 5;
    return [...new Set(sections.filter(Boolean))].slice(0, max);
  }

  async fetchCartSections(sectionIds) {
    const max = window.FoxTheme?.cartUi?.MAX_SECTIONS || 5;
    const ids = [...new Set((sectionIds || []).filter(Boolean))].slice(0, max);
    if (ids.length === 0) return {};
    const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
    const url = `${root}?sections=${encodeURIComponent(ids.join(','))}`;
    const res = await fetch(url, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!res.ok) throw new Error(`Section fetch failed (${res.status})`);
    return res.json();
  }

  async ensureCartSections(cart, sectionIds) {
    if (cart?.sections && Object.keys(cart.sections).length > 0) return cart;
    try {
      const sections = await this.fetchCartSections(sectionIds);
      cart.sections = sections;
    } catch (error) {
      this.log('ensureCartSections failed', error);
    }
    return cart;
  }

  publishCartLifecycle(cart) {
    this._publishingCart = true;
    try {
      this.applyCartReservations(cart);
      this.applyCampaignCartState(cart);
      this.pruneIneligibleMixSelections();
      this.syncFlavorUi();
      this.syncIncludedLines();
      this.syncOfferPrices();
      this.updateUI();

      if (window.FoxTheme?.cartUi?.publish) {
        window.FoxTheme.cartUi.publish(cart);
        window.FoxTheme.cartUi.scheduleStabilize();
        return;
      }
      if (window.FoxTheme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
        window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
      }
    } finally {
      this._publishingCart = false;
    }
  }

  suppressNativeStickyAtc() {
    const apply = () => {
      const section = document.getElementById('shopify-section-sticky-atc-bar');
      if (!section) return;
      section.hidden = true;
      section.setAttribute('data-ppo-suppressed', 'true');
      section.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('sticky-atc-bar-enabled');
      document.documentElement.style.removeProperty('--sticky-atc-bar-height');
    };
    apply();
    requestAnimationFrame(apply);
    this._stickySuppressTimers = [setTimeout(apply, 250), setTimeout(apply, 1000)];
  }

  restoreNativeStickyAtc() {
    (this._stickySuppressTimers || []).forEach((id) => clearTimeout(id));
    this._stickySuppressTimers = [];

    const section = document.getElementById('shopify-section-sticky-atc-bar');
    if (!section || section.getAttribute('data-ppo-suppressed') !== 'true') return;

    section.hidden = false;
    section.removeAttribute('data-ppo-suppressed');
    section.removeAttribute('aria-hidden');

    if (section.querySelector('sticky-atc-bar')) {
      document.body.classList.add('sticky-atc-bar-enabled');
    }
  }

  log(...args) {
    if (this.debugEnabled) console.log('[pdp-purchase-options]', ...args);
  }

  readVariantCatalog() {
    const script =
      this.parentElement?.querySelector('[data-ppo-variants]') || document.querySelector('[data-ppo-variants]');
    let list = [];
    try {
      list = script ? JSON.parse(script.textContent || '[]') : [];
    } catch (error) {
      this.log('variant catalog parse failed', error);
    }

    list.forEach((row) => {
      const normalized = this.normalizeVariant(row);
      if (!normalized?.id) return;
      this.variantsById.set(String(normalized.id), normalized);
      this.variantAvailability.set(String(normalized.id), Boolean(normalized.available));
    });

    this.querySelectorAll('[data-variant-id]').forEach((row) => {
      const id = row.dataset.variantId;
      if (!id) return;
      const availableAttr = row.dataset.available;
      const qtyRaw = row.dataset.inventoryQuantity;
      const hasQtyAttr = qtyRaw != null && qtyRaw !== '';
      const qtyAttr = hasQtyAttr ? Number(qtyRaw) : NaN;
      const existing = this.variantsById.get(id);

      if (existing) {
        // DOM flavor rows win over JSON for inventory/availability on the same
        // SSR page. Prevents a stale data-ppo-variants payload from overriding
        // authoritative Liquid attrs rendered beside the mixer controls.
        const next = this.normalizeVariant({
          id: existing.id,
          title: existing.title || row.dataset.variantTitle || '',
          available: availableAttr != null ? availableAttr !== 'false' : existing.available,
          price: existing.price,
          inventoryQuantity: Number.isFinite(qtyAttr) ? qtyAttr : existing.inventoryQuantity,
          inventoryManagement: row.dataset.inventoryManagement || existing.inventoryManagement,
          inventoryPolicy: row.dataset.inventoryPolicy || existing.inventoryPolicy,
          featuredMedia: existing.featuredMedia,
          pickerMedia: existing.pickerMedia,
        });
        this.variantsById.set(id, next);
        this.variantAvailability.set(id, Boolean(next.available));
        return;
      }

      this.variantAvailability.set(id, availableAttr !== 'false');
      this.variantsById.set(
        id,
        this.normalizeVariant({
          id: Number(id),
          title: row.dataset.variantTitle || '',
          available: availableAttr !== 'false',
          price: this.unitPrice,
          inventoryQuantity: Number.isFinite(qtyAttr) ? qtyAttr : 0,
          inventoryManagement: row.dataset.inventoryManagement || null,
          inventoryPolicy: row.dataset.inventoryPolicy || null,
          featuredMedia: null,
          pickerMedia: null,
        })
      );
    });
  }

  normalizeVariant(raw) {
    if (!raw || raw.id == null) return null;
    const id = Number(raw.id);
    const fromCatalog = this.variantsById.get(String(id));

    const featuredRaw =
      raw.featuredMedia ||
      raw.featured_media ||
      raw.featured_image ||
      fromCatalog?.featuredMedia ||
      null;

    const pickerRaw = raw.pickerMedia || raw.picker_media || fromCatalog?.pickerMedia || null;

    let src = null;
    let alt = '';
    if (typeof featuredRaw === 'string') {
      src = featuredRaw;
    } else if (featuredRaw && typeof featuredRaw === 'object') {
      src =
        featuredRaw.src ||
        featuredRaw.url ||
        featuredRaw.preview_image?.src ||
        featuredRaw.preview_image?.url ||
        null;
      alt = featuredRaw.alt || '';
    }

    let pickerSrc = null;
    let pickerAlt = '';
    if (typeof pickerRaw === 'string') {
      pickerSrc = pickerRaw;
    } else if (pickerRaw && typeof pickerRaw === 'object') {
      pickerSrc = pickerRaw.src || pickerRaw.url || null;
      pickerAlt = pickerRaw.alt || '';
    }

    const available =
      typeof raw.available === 'boolean'
        ? raw.available
        : typeof fromCatalog?.available === 'boolean'
          ? fromCatalog.available
          : true;

    const inventoryManagement =
      raw.inventoryManagement ??
      raw.inventory_management ??
      fromCatalog?.inventoryManagement ??
      null;
    const inventoryPolicy =
      raw.inventoryPolicy ?? raw.inventory_policy ?? fromCatalog?.inventoryPolicy ?? null;
    const inventoryQuantity = Number(
      raw.inventoryQuantity ?? raw.inventory_quantity ?? fromCatalog?.inventoryQuantity ?? 0
    );

    return {
      id,
      title: raw.title || raw.public_title || fromCatalog?.title || '',
      available,
      price: Number(raw.price ?? fromCatalog?.price ?? this.unitPrice ?? 0),
      unitPrice: Number(raw.unitPrice ?? raw.unit_price ?? fromCatalog?.unitPrice ?? 0),
      unitPriceMeasurement:
        raw.unitPriceMeasurement || raw.unit_price_measurement || fromCatalog?.unitPriceMeasurement || null,
      inventoryQuantity: Number.isFinite(inventoryQuantity) ? inventoryQuantity : 0,
      inventoryManagement: inventoryManagement || null,
      inventoryPolicy: inventoryPolicy || null,
      featuredMedia: src ? { src, alt } : fromCatalog?.featuredMedia || null,
      pickerMedia: pickerSrc ? { src: pickerSrc, alt: pickerAlt } : fromCatalog?.pickerMedia || null,
    };
  }

  offerMetrics(offer) {
    const isSingle = offer?.dataset.offerKind === 'single';
    const pack = isSingle ? 1 : Number(offer?.dataset.packSize || 1);
    const pct = isSingle ? 0 : Number(offer?.dataset.discountPercent || 0);

    const freeQty = Math.max(0, Number(offer?.dataset.bonusFreeQty || 0));
    const paidAttr = Number(offer?.dataset.bonusPaidQty || 0);
    const billableQty =
      !isSingle && freeQty > 0 ? Math.max(0, paidAttr || pack - freeQty) : pack;

    // Mixer + variant_mix: price from allocated flavour units (selected offer only).
    // buy2get1: Shopify BXGY frees the cheapest freeQty units — not a proportional share.
    if (!isSingle && this.usesVariantAllocator(offer) && offer === this.selectedOffer) {
      if (freeQty > 0) {
        const priced = this.allocationBxgyPriceCents(pack, freeQty);
        return {
          isSingle,
          pack,
          pct: 0,
          standalonePct: 0,
          compareCents: priced.compareCents,
          saleCents: priced.saleCents,
          discountedUnitCents: pack > 0 ? Math.round(priced.saleCents / pack) : 0,
        };
      }
      const priced = this.allocationPriceCents(pack, pct);
      return {
        isSingle,
        pack,
        pct,
        standalonePct: pct,
        compareCents: priced.compareCents,
        saleCents: priced.saleCents,
        discountedUnitCents: pack > 0 ? Math.round(priced.saleCents / pack) : 0,
      };
    }

    const compareCents = Math.round(Number(this.unitPrice) || 0) * pack;
    // Mirror CT discountedUnitPrice: round half-up per unit, then × pack.
    const unitCents = Math.round(Number(this.unitPrice) || 0);
    const discountedUnitCents =
      pct > 0 ? Math.round((unitCents * (100 - pct)) / 100) : unitCents;
    // buy2get1 Quantity: bill paid units at full unit price (no %); compare keeps full pack.
    const saleUnitCents = freeQty > 0 ? unitCents : discountedUnitCents;
    const saleCents = saleUnitCents * Math.max(0, billableQty);
    return {
      isSingle,
      pack,
      pct: freeQty > 0 ? 0 : pct,
      standalonePct: freeQty > 0 ? 0 : pct,
      compareCents,
      saleCents,
      discountedUnitCents: freeQty > 0 ? unitCents : discountedUnitCents,
    };
  }

  /**
   * Expand flavourCounts (+ remaining at current unit price) into a flat unit-price list.
   */
  allocationUnitPriceList(pack) {
    const units = [];
    let allocated = 0;
    this.flavorCounts.forEach((quantity, variantId) => {
      if (quantity <= 0) return;
      const unit = Math.round(
        Number(this.variantsById.get(String(variantId))?.price || this.unitPrice || 0)
      );
      const qty = Number(quantity) || 0;
      for (let i = 0; i < qty; i += 1) units.push(unit);
      allocated += qty;
    });
    const remaining = Math.max(0, Number(pack) - allocated);
    const fallback = Math.round(Number(this.unitPrice) || 0);
    for (let i = 0; i < remaining; i += 1) units.push(fallback);
    return units;
  }

  /**
   * BXGY-aligned allocator pricing: cheapest freeQty units are free (Shopify automatic discount).
   */
  allocationBxgyPriceCents(pack, freeQty) {
    const units = this.allocationUnitPriceList(pack);
    units.sort((a, b) => a - b);
    const compareCents = units.reduce((sum, unit) => sum + unit, 0);
    const freeCount = Math.min(Math.max(0, Number(freeQty) || 0), units.length);
    let freeCents = 0;
    for (let i = 0; i < freeCount; i += 1) freeCents += units[i];
    return { compareCents, saleCents: Math.max(0, compareCents - freeCents) };
  }

  /**
   * Sum allocated (and remaining) tray unit prices, applying tier % per unit (half-up).
   * Used when buy2get1 is not active (standard percentage packs).
   */
  allocationPriceCents(pack, pct) {
    let compareCents = 0;
    let saleCents = 0;
    let allocated = 0;
    this.flavorCounts.forEach((quantity, variantId) => {
      if (quantity <= 0) return;
      const unit = Math.round(
        Number(this.variantsById.get(String(variantId))?.price || this.unitPrice || 0)
      );
      const discounted = pct > 0 ? Math.round((unit * (100 - pct)) / 100) : unit;
      compareCents += unit * quantity;
      saleCents += discounted * quantity;
      allocated += Number(quantity);
    });
    const remaining = Math.max(0, Number(pack) - allocated);
    if (remaining > 0) {
      const unit = Math.round(Number(this.unitPrice) || 0);
      const discounted = pct > 0 ? Math.round((unit * (100 - pct)) / 100) : unit;
      compareCents += unit * remaining;
      saleCents += discounted * remaining;
    }
    return { compareCents, saleCents };
  }

  /**
   * Concise pack chip. Cart-context attribution lives in the note, not the chip.
   */
  formatDiscountChip(pct) {
    if (!(pct > 0)) return '';
    return `−${pct} %`;
  }

  /**
   * Eligible cart units only drive the explanatory note — never card %/gifts/prices.
   */
  syncCartContextNote() {
    if (!this.cartContextEl) return;
    const units = Number(this.eligibleCartUnits) || 0;
    if (!(this.campaignConfig?.enabled === true) || units <= 0) {
      this.cartContextEl.hidden = true;
      this.cartContextEl.textContent = '';
      return;
    }
    const template = this.cartContextTemplate || this.dataset.cartContextTemplate || '';
    this.cartContextEl.hidden = false;
    this.cartContextEl.textContent = template
      ? template.replaceAll('__COUNT__', String(units))
      : String(units);
  }

  syncOfferPrices() {
    this.offers.forEach((offer) => {
      const metrics = this.offerMetrics(offer);
      const { pct, compareCents, saleCents } = metrics;
      const saleEl = offer.querySelector('[data-offer-price] [data-sale]');
      const compareEl = offer.querySelector('[data-offer-price] [data-compare]');
      const chipEl = offer.querySelector('[data-offer-chip]');
      if (saleEl) saleEl.textContent = this.formatMoney(saleCents);
      if (compareEl) {
        // Only strike when compare is actually higher than sale (never duplicate equal prices).
        if (compareCents > saleCents) {
          compareEl.hidden = false;
          compareEl.textContent = this.formatMoney(compareCents);
        } else {
          compareEl.hidden = true;
        }
      }
      if (chipEl) {
        if (pct > 0 && compareCents > saleCents) {
          chipEl.hidden = false;
          chipEl.classList.remove('ppo__chip--cart');
          chipEl.textContent = this.formatDiscountChip(pct);
        } else {
          chipEl.hidden = true;
          chipEl.classList.remove('ppo__chip--cart');
          chipEl.textContent = '';
        }
      }
    });
  }

  syncSummaryMeta(variant) {
    if (!this.summaryUnit) return;
    const measurement = variant?.unitPriceMeasurement;
    const unitPrice = Number(variant?.unitPrice || 0);

    if (measurement && unitPrice > 0) {
      const refValue = Number(measurement.referenceValue || measurement.reference_value || 1);
      const refUnit = measurement.referenceUnit || measurement.reference_unit || '';
      const unitLabel = refValue !== 1 ? `${refValue}${refUnit}` : refUnit;
      this.summaryUnit.textContent = `${this.formatMoney(unitPrice)}/${unitLabel}`;
    } else {
      this.summaryUnit.textContent = '';
    }
  }

  resolveBootstrapVariant() {
    const id = this.getSelectedVariantId();
    if (id && this.variantsById.has(String(id))) return this.variantsById.get(String(id));
    const first = this.variantsById.values().next().value;
    return first || null;
  }

  /** Flavour-identity art for selector / mixer / Single (not product photography). */
  pickerSrcForVariant(variant) {
    if (!variant) return '';
    return (
      variant.pickerMedia?.src ||
      this.variantsById.get(String(variant.id))?.pickerMedia?.src ||
      ''
    );
  }

  mediaSrcForVariant(variant) {
    // Variant featured only — never product featured (avoids Maracuja on Peach/KiBa).
    return variant?.featuredMedia?.src || '';
  }

  /**
   * Sync Single + pack cards to PDP flavor.
   * Does not reset allocator quantities (mixer or variant_mix).
   * Does not rewrite mixer thumbnails.
   * Top-flavour changes keep controlling Single only; an edited variant_mix
   * allocation is never silently overwritten.
   */
  handleVariantChange(rawVariant, meta = {}) {
    const variant = this.normalizeVariant(rawVariant);
    if (!variant?.id) return;

    this.log('variantChange', meta.source, variant.id, variant.title);

    this.currentVariant = variant;
    this.unitPrice = Number(variant.price || this.unitPrice || 0);
    this.variantAvailability.set(String(variant.id), Boolean(variant.available));
    if (this.variantsById.has(String(variant.id))) {
      this.variantsById.set(String(variant.id), {
        ...this.variantsById.get(String(variant.id)),
        ...variant,
        featuredMedia: variant.featuredMedia || this.variantsById.get(String(variant.id)).featuredMedia,
        pickerMedia: variant.pickerMedia || this.variantsById.get(String(variant.id)).pickerMedia,
      });
    } else {
      this.variantsById.set(String(variant.id), variant);
    }

    const fallbackId = this.parentElement?.querySelector('[data-ppo-fallback-id]');
    if (fallbackId) fallbackId.value = String(variant.id);

    this.syncOfferPrices();
    this.syncSingleOffer(variant);
    this.syncPackImagery(variant);
    this.pruneIneligibleMixSelections();

    // Late bootstrap / empty seed only — never clobber a shopper-edited mix.
    if (
      this.isVariantMixOffer(this.selectedOffer) &&
      !this.mixAllocationTouched &&
      this.selectedQty() === 0
    ) {
      this.seedVariantMixAllocation(this.selectedOffer);
    }

    this.syncFlavorUi();
    this.syncIncludedLines();
    this.syncSummaryMeta(variant);
    this.updateUI();
  }

  pruneIneligibleMixSelections() {
    let changed = false;
    this.flavorCounts.forEach((qty, id) => {
      if (qty <= 0) return;
      if (!this.isVariantAllowed(id)) {
        this.flavorCounts.set(id, 0);
        changed = true;
        return;
      }
      const cap = this.getInventoryCap(id);
      if (cap != null && qty > cap) {
        this.flavorCounts.set(id, Math.max(0, cap));
        changed = true;
      }
    });
    return changed;
  }

  syncSingleOffer(variant) {
    const single = this.offers.find((o) => o.dataset.offerKind === 'single');
    if (!single) return;

    single.classList.toggle('is-single-unavailable', !this.isVariantAvailable(variant.id));
    single.dataset.selectedVariantId = String(variant.id);

    const sub = single.querySelector('[data-single-sub]');
    if (sub) {
      const template = this.dataset.singleSubTemplate || '1 Stück · {variant}';
      const productTitle = this.dataset.productTitle || '';
      const variantTitle = variant?.title || '';
      const hasProductToken = template.includes('{product}');
      const hasVariantToken = template.includes('{variant}');
      let text = template
        .replace(/\{product\}/g, productTitle)
        .replace(/\{variant\}/g, variantTitle);
      if (!hasProductToken && !hasVariantToken && variantTitle) {
        text = `${template} · ${variantTitle}`;
      } else if (hasVariantToken && !variantTitle) {
        text = template
          .replace(/ · \{variant\}/g, '')
          .replace(/\{variant\}/g, 'freier Geschmack oben')
          .replace(/\{product\}/g, productTitle);
      }
      sub.textContent = text;
    }

    const mediaHost = single.querySelector('[data-offer-media]');
    // Product pack Single override is static — do not replace with flavour art.
    if (single.dataset.mediaMode === 'static') return;
    if (mediaHost) this.renderPackMedia(mediaHost, 1, this.pickerSrcForVariant(variant));
  }

  syncPackImagery(variant) {
    const src = this.pickerSrcForVariant(variant);
    this.offers.forEach((offer) => {
      if (offer.dataset.offerKind === 'single') return;
      if (offer.dataset.mediaMode === 'static') return;
      const pack = Number(offer.dataset.mediaPackSize || offer.dataset.packSize || 1);
      const mediaHost = offer.querySelector('[data-offer-media]');
      if (mediaHost) this.renderPackMedia(mediaHost, pack, src);
    });
  }

  renderPackMedia(host, packSize, src) {
    const offer = host?.closest?.('[data-offer]');
    const mediaPack = Number(offer?.dataset?.mediaPackSize || packSize || 1);
    const n = Math.max(1, Math.min(4, mediaPack || Number(packSize) || 1));
    if (!src) {
      host.innerHTML = `<span class="ppo__packs">${'<i class="ppo__pack"></i>'.repeat(n)}</span>`;
      return;
    }
    const safe = this.escapeHtml(src);
    if (n === 1) {
      host.innerHTML = `<img src="${safe}" alt="" width="52" height="52" loading="lazy" decoding="async">`;
      return;
    }
    const photos = Array.from({ length: n }, () => {
      return `<img class="ppo__pack-photo" src="${safe}" alt="" width="32" height="40" loading="lazy" decoding="async">`;
    }).join('');
    host.innerHTML = `<span class="ppo__packs ppo__packs--photos">${photos}</span>`;
  }

  isVariantAvailable(variantId) {
    if (this.forceUnavailable) return false;
    if (!variantId) return false;
    if (!this.variantAvailability.has(String(variantId))) return true;
    return this.variantAvailability.get(String(variantId)) === true;
  }

  /**
   * Max selectable qty when inventory is tracked and overselling is denied.
   * remaining = max(0, page-load raw inventory − flavor units already in cart).
   * null = no client-side cap (untracked or continue selling).
   */
  getInventoryCap(variantId) {
    if (!variantId) return null;
    const variant = this.variantsById.get(String(variantId));
    if (!variant) return null;
    const management = String(variant.inventoryManagement || '').toLowerCase();
    if (management !== 'shopify') return null;
    const policy = String(variant.inventoryPolicy || 'deny').toLowerCase();
    if (policy === 'continue') return null;
    const raw = Number(variant.inventoryQuantity);
    if (!Number.isFinite(raw)) return 0;
    const reserved = this.cartReservations.get(String(variantId)) || 0;
    return Math.max(0, raw - reserved);
  }

  formatRemainingLabel(n) {
    return String(this.remainingLabelTemplate || 'Nur noch {n} verfügbar').replace('{n}', String(n));
  }

  isQuantityOffer(offer = this.selectedOffer) {
    if (!offer) return false;
    if (this.usesVariantAllocator(offer)) return false;
    if (offer.dataset.offerKind === 'quantity') return true;
    return offer.dataset.offerKind !== 'single';
  }

  /** Beauty Drink mixer only — never treat variant_mix as mixer. */
  isMixerOffer(offer = this.selectedOffer) {
    if (!offer) return false;
    return offer.dataset.offerKind === 'mixer';
  }

  /** Bar tray allocator (quantity profile + selection_strategy=variant_mix). */
  isVariantMixOffer(offer = this.selectedOffer) {
    if (!offer) return false;
    return (
      offer.dataset.offerKind === 'variant-mix' ||
      offer.dataset.selectionStrategy === 'variant_mix'
    );
  }

  /** Shared flavour +/- UI: Beauty Drink mixer or Bar variant_mix. */
  usesVariantAllocator(offer = this.selectedOffer) {
    return this.isMixerOffer(offer) || this.isVariantMixOffer(offer);
  }

  selectionPurchasable() {
    if (this.forceUnavailable) return false;
    const offer = this.selectedOffer;
    if (!offer) return false;

    // Single + Quantity: one selected PDP variant, qty = pack size (1 for single).
    if (!this.usesVariantAllocator(offer)) {
      const variantId = this.getSelectedVariantId();
      if (!this.isVariantAvailable(variantId)) return false;
      const need = Number(offer.dataset.packSize || 1);
      const cap = this.getInventoryCap(variantId);
      if (cap != null && cap < need) return false;
      return true;
    }

    let any = false;
    for (const [variantId, quantity] of this.flavorCounts.entries()) {
      if (quantity <= 0) continue;
      any = true;
      if (!this.isVariantAvailable(variantId)) return false;
      const cap = this.getInventoryCap(variantId);
      if (cap != null && quantity > cap) return false;
    }
    return any;
  }

  hasPurchasableInventory() {
    if (this.forceUnavailable) return false;
    const offer = this.selectedOffer;
    if (!offer) return false;

    if (!this.usesVariantAllocator(offer)) {
      const variantId = this.getSelectedVariantId();
      if (!this.isVariantAvailable(variantId)) return false;
      const need = Number(offer.dataset.packSize || 1);
      const cap = this.getInventoryCap(variantId);
      return cap == null || cap >= need;
    }

    const rows = this.sharedMixer?.querySelectorAll('[data-variant-id]') || [];
    for (const row of rows) {
      const id = row.dataset.variantId;
      if (!id || !this.isVariantAllowed(id) || !this.isVariantAvailable(id)) continue;
      const cap = this.getInventoryCap(id);
      if (cap == null || cap > 0) return true;
    }
    return false;
  }

  formatMoney(cents) {
    if (window.FoxTheme?.Currency?.formatMoney) {
      return window.FoxTheme.Currency.formatMoney(cents, this.moneyFormat);
    }
    const amount = (Number(cents) / 100).toFixed(2);
    const code = this.currencyCode || window.Shopify?.currency?.active || '';
    return code ? `${amount} ${code}` : amount;
  }

  get selectedOffer() {
    return this.offers.find((o) => o.querySelector('input[type="radio"]')?.checked) || null;
  }

  selectOffer(offer, { resetMix = true } = {}) {
    this.offers.forEach((o) => {
      const selected = o === offer;
      o.classList.toggle('is-selected', selected);
      const panel = o.querySelector('[data-inline-panel]');
      if (panel) {
        const showPanel =
          selected &&
          (o.dataset.offerKind === 'single' ||
            o.dataset.offerKind === 'quantity' ||
            this.usesVariantAllocator(o));
        panel.hidden = !showPanel;
      }
      const input = o.querySelector('input[type="radio"]');
      if (input) input.checked = selected;
    });

    if (resetMix) {
      this.mixAllocationTouched = false;
      this.flavorCounts.forEach((_, id) => this.flavorCounts.set(id, 0));
      if (this.isVariantMixOffer(offer)) {
        this.seedVariantMixAllocation(offer);
      }
    }

    this.mountSharedMixer(offer);
    this.syncFlavorUi();
    this.syncIncludedLines();
    const metrics = this.offerMetrics(offer);
    this.syncSummary(offer, metrics);
    this.updateUI();
  }

  /**
   * Deterministic reset: put the full pack on the current top flavour (or first available).
   */
  seedVariantMixAllocation(offer = this.selectedOffer) {
    if (!this.isVariantMixOffer(offer)) return;
    const pack = Number(offer?.dataset.packSize || 1);
    this.flavorCounts.forEach((_, id) => this.flavorCounts.set(id, 0));

    const preferredId = String(this.getSelectedVariantId() || '');
    let seedId = null;
    if (preferredId && this.isVariantAllowed(preferredId) && this.isVariantAvailable(preferredId)) {
      seedId = preferredId;
    } else {
      const rows = this.sharedMixer?.querySelectorAll('[data-variant-id]') || [];
      for (const row of rows) {
        const id = row.dataset.variantId;
        if (id && this.isVariantAllowed(id) && this.isVariantAvailable(id)) {
          seedId = id;
          break;
        }
      }
    }
    if (!seedId || !(pack > 0)) return;

    let next = pack;
    const cap = this.getInventoryCap(seedId);
    if (cap != null) next = Math.min(next, cap);
    this.flavorCounts.set(String(seedId), next);
  }

  packSize() {
    return Number(this.selectedOffer?.dataset.packSize || 1);
  }

  selectedQty() {
    let total = 0;
    this.flavorCounts.forEach((q) => {
      total += q;
    });
    return total;
  }

  isVariantAllowed(variantId) {
    const allowed = (this.selectedOffer?.dataset.allowedVariants || '').trim();
    if (!allowed) return true;
    return allowed
      .split(',')
      .map((s) => s.trim())
      .includes(String(variantId));
  }

  adjustQty(variantId, delta) {
    if (!variantId || !this.selectedOffer || !this.usesVariantAllocator(this.selectedOffer)) return;
    if (!this.isVariantAllowed(variantId)) return;
    if (delta > 0 && !this.isVariantAvailable(variantId)) return;

    const current = this.flavorCounts.get(variantId) || 0;
    const pack = this.packSize();
    const total = this.selectedQty();
    let next = current + delta;
    if (next < 0) next = 0;
    if (delta > 0 && total >= pack) return;
    if (delta > 0 && total + delta > pack) next = current + (pack - total);

    const cap = this.getInventoryCap(variantId);
    if (cap != null && next > cap) {
      if (current >= cap) return;
      next = cap;
    }

    this.flavorCounts.set(variantId, next);
    if (this.isVariantMixOffer(this.selectedOffer)) this.mixAllocationTouched = true;
    this.syncFlavorUi();
    this.syncIncludedLines();
    this.updateUI();
  }

  syncFlavorUi() {
    const rows = this.sharedMixer?.querySelectorAll('[data-variant-id]') || [];
    const pack = this.packSize();
    const total = this.selectedQty();
    rows.forEach((row) => {
      const id = row.dataset.variantId;
      const value = this.flavorCounts.get(id) || 0;
      const available = this.isVariantAvailable(id);
      const cap = this.getInventoryCap(id);
      const atCap = cap != null && value >= cap;
      const el = row.querySelector('[data-qty-value]');
      if (el) el.textContent = String(value);
      row.classList.toggle('is-active', value > 0);
      row.classList.toggle('is-unavailable', !available);
      row.classList.toggle('is-at-cap', available && atCap);

      const meta = row.querySelector('.ppo__flavor-meta');
      const showRemaining =
        available && cap != null && cap > 0 && (cap <= pack || atCap || cap <= 3);
      if (!available) {
        if (!meta) {
          const copy = row.querySelector('.ppo__flavor-copy');
          if (copy) {
            const span = document.createElement('span');
            span.className = 'ppo__flavor-meta';
            span.textContent = this.soldOutLabel;
            copy.appendChild(span);
          }
        } else {
          meta.textContent = this.soldOutLabel;
        }
      } else if (showRemaining) {
        const label = this.formatRemainingLabel(cap);
        if (!meta) {
          const copy = row.querySelector('.ppo__flavor-copy');
          if (copy) {
            const span = document.createElement('span');
            span.className = 'ppo__flavor-meta';
            span.textContent = label;
            copy.appendChild(span);
          }
        } else {
          meta.textContent = label;
        }
      } else if (meta) {
        meta.remove();
      }

      const plusDisabled = !available || atCap || total >= pack;
      row.querySelectorAll('[data-qty-plus], [data-qty-minus]').forEach((btn) => {
        if (btn.hasAttribute('data-qty-plus')) btn.disabled = plusDisabled;
        if (btn.hasAttribute('data-qty-minus')) btn.disabled = value <= 0;
      });
    });
  }

  escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  incCheckIcon() {
    return '<span class="ppo__inc-icon" aria-hidden="true"><svg class="ppo__icon ppo__icon--check" width="12" height="12" aria-hidden="true" focusable="false"><use href="#ppo-icon-check"></use></svg></span>';
  }

  syncBonusFreeRowThumb(offer, src) {
    const freeRow = offer?.querySelector('[data-bonus-free-row]');
    if (!freeRow) return;
    const thumbHost = freeRow.querySelector('.ppo__inc-thumb');
    if (!thumbHost) return;
    const resolved =
      src ||
      this.pickerSrcForVariant(this.currentVariant) ||
      this.dataset.productImage ||
      '';
    if (!resolved) {
      thumbHost.innerHTML = '';
      return;
    }
    const alt = this.escapeHtml(this.dataset.productTitle || '');
    thumbHost.innerHTML = `<img src="${this.escapeHtml(resolved)}" alt="${alt}" width="28" height="28" loading="lazy" decoding="async">`;
  }

  syncIncludedLines() {
    const offer = this.selectedOffer;
    if (!offer) return;
    const host = offer.querySelector('[data-included-lines]');
    if (!host) return;

    const pack = this.packSize();
    const allocatable = this.usesVariantAllocator(offer);
    const productTitle = this.dataset.productTitle || 'Produkt';

    if (!allocatable) {
      const variant = this.currentVariant;
      const preferProductTitle = this.dataset.includedLabel === 'product';
      const title = preferProductTitle
        ? productTitle
        : variant?.title && variant.title !== 'Default Title'
          ? variant.title
          : productTitle;
      const freeQty = Math.max(0, Number(offer.dataset.bonusFreeQty || 0));
      const paidAttr = Number(offer.dataset.bonusPaidQty || 0);
      const paidQty = freeQty > 0 ? Math.max(0, paidAttr || pack - freeQty) : pack;
      const qty = Math.max(1, paidQty > 0 ? paidQty : pack);
      const img = this.pickerSrcForVariant(variant) || this.dataset.productImage || '';
      const thumb = img
        ? `<img src="${this.escapeHtml(img)}" alt="" width="28" height="28" loading="lazy" decoding="async">`
        : '<span class="ppo__inc-thumb" aria-hidden="true"></span>';
      host.innerHTML = `<span class="ppo__inc-row">${this.incCheckIcon()}${thumb}<span class="ppo__inc-lbl">${qty}× ${this.escapeHtml(title)}</span></span>`;
      this.syncBonusFreeRowThumb(offer, img);
      return;
    }

    const parts = [];
    this.flavorCounts.forEach((quantity, variantId) => {
      if (quantity <= 0) return;
      const catalog = this.variantsById.get(String(variantId));
      const row = this.sharedMixer?.querySelector(`[data-variant-id="${variantId}"]`);
      const title = catalog?.title || row?.dataset.variantTitle || productTitle;
      const img =
        catalog?.pickerMedia?.src ||
        row?.querySelector('.ppo__flavor-image')?.getAttribute('src') ||
        '';
      const thumb = img
        ? `<img src="${this.escapeHtml(img)}" alt="" width="28" height="28" loading="lazy" decoding="async">`
        : '<span class="ppo__inc-thumb" aria-hidden="true"></span>';
      parts.push(
        `<span class="ppo__inc-row">${this.incCheckIcon()}${thumb}<span class="ppo__inc-lbl">${quantity}× ${this.escapeHtml(title)}</span></span>`
      );
    });

    if (parts.length === 0) {
      const mutedTpl = this.isVariantMixOffer(offer)
        ? this.dataset.mutedDistributeTrays || `${pack}× Tray`
        : this.dataset.mutedChooseMix || `${pack}× Beauty Drink`;
      const muted = mutedTpl.replace(/__COUNT__/g, String(pack));
      host.innerHTML = `<span class="ppo__inc-row ppo__inc-row--muted">${muted}</span>`;
      return;
    }

    host.innerHTML = parts.join('');
  }

  syncSummary(offer, metrics) {
    const { isSingle, pct, compareCents, saleCents } = metrics;
    if (this.summarySale) this.summarySale.textContent = this.formatMoney(saleCents);
    if (this.summaryCompare) {
      if (!isSingle && compareCents > saleCents) {
        this.summaryCompare.hidden = false;
        this.summaryCompare.textContent = this.formatMoney(compareCents);
      } else {
        this.summaryCompare.hidden = true;
        this.summaryCompare.textContent = '';
      }
    }
    if (this.summaryChip) {
      if (!isSingle && pct > 0 && compareCents > saleCents) {
        this.summaryChip.hidden = false;
        this.summaryChip.classList.remove('ppo__chip--cart');
        this.summaryChip.textContent = this.formatDiscountChip(pct);
      } else {
        this.summaryChip.hidden = true;
        this.summaryChip.classList.remove('ppo__chip--cart');
        this.summaryChip.textContent = '';
      }
    }
  }

  parseGiftDisplay(offer) {
    const raw = offer?.dataset.giftDisplay;
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }

  valueMetrics(offer, metrics, giftItems) {
    const shipping = 0;
    // Theme kill-switch / buy2get1: Gesamtwert + Du sparst = pack gap only (never Mixer/Cup UVP).
    if ((this.dataset.suppressCampaignGifts || '').trim() === 'true') {
      const total = metrics.compareCents + shipping;
      const saved = metrics.compareCents - metrics.saleCents + shipping;
      return { total, saved, giftTotal: 0, shipping };
    }
    if (this.hasCampaignGifts()) {
      const gifts = giftItems || offer?._displayGiftItems || [];
      const giftTotal = ppoGiftItemsValueCents(gifts);
      const total = metrics.compareCents + giftTotal + shipping;
      const saved = metrics.compareCents - metrics.saleCents + giftTotal + shipping;
      return { total, saved, giftTotal, shipping };
    }
    const gifts = giftItems || this.parseGiftDisplay(offer);
    const giftTotal = gifts.reduce((sum, gift) => sum + Number(gift.price || 0), 0);
    const total = metrics.compareCents + giftTotal + shipping;
    const saved = total - metrics.saleCents;
    return { total, saved, giftTotal, shipping };
  }

  syncValueFooter(offer, metrics) {
    const valueHost = offer?.querySelector('[data-included-value]');
    if (!valueHost) return;

    const { isSingle, pack, pct, compareCents, saleCents } = metrics;
    const canShowConfiguredValues = offer?.dataset.valueConfigured === 'true';
    const { total, saved, giftTotal } = this.valueMetrics(offer, metrics);
    const hasPctSavings = pct > 0 && compareCents > saleCents;
    const hasGiftSavings = giftTotal > 0;
    const hasPromoSavings = compareCents > saleCents;
    // Show on packs with %, BXGY free-unit gap, and/or gift UVP savings.
    const showFooter =
      canShowConfiguredValues &&
      (hasPctSavings || hasGiftSavings || hasPromoSavings) &&
      (!isSingle || hasGiftSavings || hasPctSavings || hasPromoSavings) &&
      (pack >= 1);

    if (showFooter && (pack > 1 || hasGiftSavings)) {
      valueHost.hidden = false;
      const totalEl = valueHost.querySelector('[data-value-total]');
      const savedEl = valueHost.querySelector('[data-value-saved]');
      if (totalEl) totalEl.textContent = `Gesamtwert ${this.formatMoney(total)}`;
      if (savedEl) savedEl.textContent = `Du sparst ${this.formatMoney(Math.max(0, saved))}`;
    } else {
      valueHost.hidden = true;
    }
  }

  updateUI() {
    const offer = this.selectedOffer;
    if (!offer) return;

    const metrics = this.offerMetrics(offer);
    const { pack, pct, saleCents } = metrics;
    const allocatable = this.usesVariantAllocator(offer);
    const qty = this.selectedQty();
    const complete = !allocatable || qty === pack;

    if (this.ctaPrice) this.ctaPrice.textContent = this.formatMoney(saleCents);

    this.syncSummary(offer, metrics);
    this.syncAllOfferGifts();
    if (!this.hasCampaignGifts()) this.syncValueFooter(offer, metrics);
    if (this.currentVariant) this.syncSummaryMeta(this.currentVariant);
    this.syncCartContextNote();

    if (this.mixerStatus) this.mixerStatus.textContent = `${qty} / ${pack} ausgewählt`;

    this.syncIncludedLines();

    const inventoryOk = this.hasPurchasableInventory();
    const selectionOk = this.selectionPurchasable();
    const optionUnavailable = !inventoryOk || (complete && !selectionOk);

    if (this.hintEl) {
      if (optionUnavailable) {
        this.hintEl.textContent = '';
        this.hintEl.hidden = true;
      } else if (!allocatable) {
        this.hintEl.hidden = false;
        const productTitle = this.dataset.productTitle || 'Produkt';
        if (this.isQuantityOffer(offer) || pack > 1) {
          this.hintEl.textContent = `${pack}× ${productTitle}`;
        } else {
          const title = this.currentVariant?.title;
          this.hintEl.textContent = title ? `Single · ${title}` : 'Single · Geschmack über die Variantenwahl oben';
        }
      } else if (!complete) {
        this.hintEl.hidden = false;
        this.hintEl.textContent = this.dataset.incompleteLabel || '';
      } else {
        this.hintEl.hidden = false;
        this.hintEl.textContent = this.dataset.completeLabel || 'Auswahl vollständig';
      }
    }

    if (this.ctaLabel) {
      this.ctaLabel.textContent = optionUnavailable ? this.soldOutLabel : this.defaultCtaLabel;
    }

    if (this.cta) {
      this.cta.disabled = this.isSubmitting || optionUnavailable || !complete;
      this.cta.classList.toggle('is-soldout', optionUnavailable);
      this.cta.setAttribute(
        'aria-label',
        optionUnavailable ? this.soldOutLabel : `${this.defaultCtaLabel} ${this.ctaPrice?.textContent || ''}`.trim()
      );
    }
  }

  getSelectedVariantId() {
    if (this.currentVariant?.id) return Number(this.currentVariant.id);

    const sectionId = this.dataset.sectionId;
    const productInfo =
      document.querySelector(`product-info[data-section="${sectionId}"]`) ||
      document.querySelector(`#ProductInfo-${sectionId}`) ||
      document.querySelector('product-info');

    const input =
      productInfo?.querySelector(`form:not(.ppo__product-form) [name="id"]`) ||
      document.querySelector(`#product-form-${sectionId} [name="id"]`) ||
      document.querySelector('variant-selects [name="id"]') ||
      this.querySelector('[data-ppo-fallback-id]');

    const fromUrl = new URLSearchParams(window.location.search).get('variant');
    return Number(input?.value || fromUrl || 0);
  }

  buildLineItems() {
    const offer = this.selectedOffer;
    if (!offer) throw new Error('Keine Kaufoption gewählt.');

    if (offer.dataset.offerKind === 'single') {
      const variantId = this.getSelectedVariantId();
      if (!variantId) throw new Error('Bitte eine Variante wählen.');
      if (!this.isVariantAvailable(variantId)) throw new Error(this.soldOutLabel);
      const cap = this.getInventoryCap(variantId);
      if (cap != null && cap < 1) throw new Error(this.inventoryUnavailableLabel);
      return [{ id: variantId, quantity: 1 }];
    }

    // Mixer + variant_mix: ordinary sellable lines — no operational properties. BOGOS owns gifts.
    if (this.usesVariantAllocator(offer)) {
      const items = [];
      let flavorQty = 0;
      this.flavorCounts.forEach((quantity, variantId) => {
        if (quantity <= 0 || !this.isVariantAllowed(variantId)) return;
        const cap = this.getInventoryCap(variantId);
        if (cap != null && quantity > cap) {
          throw new Error(this.inventoryUnavailableLabel);
        }
        flavorQty += Number(quantity);
        items.push({
          id: Number(variantId),
          quantity,
        });
      });

      if (flavorQty !== this.packSize()) {
        throw new Error(`Bundle quantity mismatch: expected ${this.packSize()}, got ${flavorQty}.`);
      }

      if (items.length === 0) throw new Error('No bundle line items to add.');
      return items;
    }

    // Quantity packs: plain cart line qty=N (CT cart-wide ladder / BXGY physical qty).
    const variantId = this.getSelectedVariantId();
    if (!variantId) throw new Error('Bitte eine Variante wählen.');
    if (!this.isVariantAvailable(variantId)) throw new Error(this.soldOutLabel);
    const pack = this.packSize();
    const cap = this.getInventoryCap(variantId);
    if (cap != null && pack > cap) throw new Error(this.inventoryUnavailableLabel);
    return [{ id: variantId, quantity: pack }];
  }

  clearError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  }

  showError(message) {
    this.log('error', message);
    if (this.errorEl) {
      this.errorEl.hidden = false;
      this.errorEl.textContent = message;
      try {
        this.errorEl.focus({ preventScroll: false });
      } catch (_) {
        /* ignore */
      }
      return;
    }
    alert(message);
  }

  toggleSubmitting(isSubmitting) {
    this.isSubmitting = isSubmitting;
    if (this.cta) {
      this.cta.classList.toggle('btn--loading', isSubmitting);
      this.cta.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');
      this.cta.setAttribute('aria-disabled', isSubmitting ? 'true' : 'false');
    }
    this.updateUI();
  }

  cartFetchConfig(type = 'json') {
    return (
      window.FoxTheme?.utils?.fetchConfig?.(type) || {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: `application/${type}`,
        },
      }
    );
  }

  async fetchCart() {
    if (window.FoxTheme?.cartUi?.fetchCart) {
      return window.FoxTheme.cartUi.fetchCart();
    }
    const cartPath = (window.FoxTheme?.routes?.cart_url || '/cart').replace(/\.js$/i, '');
    const res = await fetch(`${cartPath}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!res.ok) throw new Error(`Cart fetch failed (${res.status})`);
    return res.json();
  }

  isFlavorVariantId(variantId) {
    return this.variantsById.has(String(variantId));
  }

  /**
   * Plain variant totals (line.variant_id × quantity). Used for ATC vector math —
   * not carrier/_bundle_units expansion (reservations keep that for legacy carts).
   */
  countPlainVariantQuantity(cart, variantId) {
    const target = String(variantId);
    let total = 0;
    for (const item of cart?.items || []) {
      if (String(item.variant_id) !== target) continue;
      const qty = Number(item.quantity || 0);
      if (qty > 0) total += qty;
    }
    return total;
  }

  expectedDeltaFromItems(items) {
    const delta = new Map();
    for (const item of items || []) {
      const id = String(item.id);
      const qty = Number(item.quantity || 0);
      if (!id || qty <= 0) continue;
      delta.set(id, (delta.get(id) || 0) + qty);
    }
    return delta;
  }

  /**
   * Snapshot targeted variants before ATC: totals by variant + existing line keys.
   */
  snapshotVariantVector(cart, variantIds) {
    const targets = new Set([...variantIds].map(String));
    const totals = new Map();
    const lines = [];

    for (const id of targets) totals.set(id, 0);

    for (const item of cart?.items || []) {
      const vid = String(item.variant_id);
      if (!targets.has(vid)) continue;
      const qty = Number(item.quantity || 0);
      if (qty <= 0) continue;
      totals.set(vid, (totals.get(vid) || 0) + qty);
      if (item.key) {
        lines.push({ key: item.key, variantId: vid, quantity: qty });
      }
    }

    return { totals, lines, targets };
  }

  verifyQuantityVector(beforeSnapshot, afterCart, expectedDelta) {
    const targetedIds = [...expectedDelta.keys()];
    const after = this.snapshotVariantVector(afterCart, targetedIds);
    const results = {};
    let shortfall = false;
    let overshoot = false;

    for (const id of targetedIds) {
      const beforeQty = beforeSnapshot.totals.get(id) || 0;
      const delta = expectedDelta.get(id) || 0;
      const expected = beforeQty + delta;
      const actual = after.totals.get(id) || 0;
      results[id] = { beforeQty, delta, expected, actual };
      if (actual < expected) shortfall = true;
      if (actual > expected) overshoot = true;
    }

    if (!shortfall && !overshoot) {
      return { ok: true, reason: 'exact', after, results, safeToRestore: false };
    }
    if (overshoot) {
      return {
        ok: false,
        reason: 'ambiguous_overshoot',
        after,
        results,
        safeToRestore: false,
      };
    }
    return {
      ok: false,
      reason: 'partial_shortfall',
      after,
      results,
      safeToRestore: true,
    };
  }

  isAtBaseline(beforeSnapshot, cart, expectedDelta) {
    for (const id of expectedDelta.keys()) {
      const floor = beforeSnapshot.totals.get(id) || 0;
      const actual = this.countPlainVariantQuantity(cart, id);
      if (actual !== floor) return false;
    }
    return true;
  }

  /**
   * Classify post-ATC cart vs expected vector.
   * States: exact | unchanged | partial | overshoot | below_baseline
   */
  classifyVectorState(beforeSnapshot, afterCart, expectedDelta) {
    const verified = this.verifyQuantityVector(beforeSnapshot, afterCart, expectedDelta);

    if (verified.ok) {
      return { ...verified, state: 'exact' };
    }

    if (verified.reason === 'ambiguous_overshoot') {
      return { ...verified, state: 'overshoot', safeToRestore: false };
    }

    let anyIncrease = false;
    let belowBaseline = false;
    for (const id of expectedDelta.keys()) {
      const beforeQty = beforeSnapshot.totals.get(id) || 0;
      const actual = verified.after?.totals?.get(id) || 0;
      if (actual > beforeQty) anyIncrease = true;
      if (actual < beforeQty) belowBaseline = true;
    }

    if (belowBaseline) {
      return { ...verified, state: 'below_baseline', safeToRestore: false };
    }
    if (!anyIncrease) {
      return { ...verified, state: 'unchanged', safeToRestore: false };
    }
    return { ...verified, state: 'partial', safeToRestore: true };
  }

  /**
   * HTTP-error / reported-failure reconciliation.
   * Shopify may partially add multi-item payloads while returning an error.
   * Exact expected delta after a reported failure is restored to baseline (not treated as success).
   */
  async reconcileFailedAdd(beforeSnapshot, expectedDelta, sectionsToBundle, errorMessage) {
    const cartJson = await this.fetchCart();
    const classified = this.classifyVectorState(beforeSnapshot, cartJson, expectedDelta);
    this.log('failedAddReconcile', classified);

    if (classified.state === 'unchanged') {
      await this.ensureCartSections(cartJson, sectionsToBundle);
      this.publishCartLifecycle(cartJson);
      this.showError(errorMessage);
      return { handled: true, state: classified.state };
    }

    if (classified.state === 'partial' || classified.state === 'exact') {
      const restored = await this.restoreQuantityVector(
        beforeSnapshot,
        expectedDelta,
        sectionsToBundle
      );
      if (!restored.ok || !this.isAtBaseline(beforeSnapshot, restored.cart, expectedDelta)) {
        this.log('failedAdd restore incomplete', restored);
        this.showError(errorMessage);
        return { handled: true, state: classified.state, restored };
      }
      this.showError(errorMessage);
      return { handled: true, state: classified.state, restored };
    }

    // overshoot / below_baseline: preserve authoritative cart, fail closed
    await this.ensureCartSections(cartJson, sectionsToBundle);
    this.publishCartLifecycle(cartJson);
    this.showError(errorMessage);
    return { handled: true, state: classified.state };
  }

  /**
   * Safe partial-add restore:
   * - Pre-existing targeted keys → previous quantities
   * - Newly created targeted lines → remove
   * Never reduce any targeted variant below its pre-ATC total.
   * Verifies exact baseline after update.
   *
   * Race hardening: reclassify the freshly fetched cart at this mutation boundary.
   * If another targeted add arrived since the caller classified, abort restore on
   * overshoot/below_baseline; no-op on unchanged; proceed only for partial/exact.
   */
  async restoreQuantityVector(beforeSnapshot, expectedDelta, sectionsToBundle) {
    const cart = await this.fetchCart();
    const classified = this.classifyVectorState(beforeSnapshot, cart, expectedDelta);
    this.log('restoreReclassify', classified);

    if (classified.state === 'unchanged') {
      await this.ensureCartSections(cart, sectionsToBundle);
      this.publishCartLifecycle(cart);
      return { ok: true, cart, reason: 'unchanged', classified };
    }

    if (classified.state === 'overshoot' || classified.state === 'below_baseline') {
      await this.ensureCartSections(cart, sectionsToBundle);
      this.publishCartLifecycle(cart);
      return {
        ok: false,
        cart,
        reason: `restore_aborted_${classified.state}`,
        classified,
      };
    }

    if (classified.state !== 'partial' && classified.state !== 'exact') {
      await this.ensureCartSections(cart, sectionsToBundle);
      this.publishCartLifecycle(cart);
      return { ok: false, cart, reason: 'restore_aborted_unknown', classified };
    }

    // Build updates from the same cart that was just classified (no second fetch).
    const targetedIds = new Set([...expectedDelta.keys()].map(String));
    const beforeKeys = new Map(
      beforeSnapshot.lines.map((line) => [line.key, Number(line.quantity || 0)])
    );
    const updates = {};

    for (const item of cart.items || []) {
      const vid = String(item.variant_id);
      if (!targetedIds.has(vid) || !item.key) continue;

      if (beforeKeys.has(item.key)) {
        const prev = beforeKeys.get(item.key);
        if (Number(item.quantity) !== prev) {
          updates[item.key] = prev;
        }
      } else {
        updates[item.key] = 0;
      }
    }

    if (Object.keys(updates).length === 0) {
      await this.ensureCartSections(cart, sectionsToBundle);
      this.publishCartLifecycle(cart);
      const atBaseline = this.isAtBaseline(beforeSnapshot, cart, expectedDelta);
      return {
        ok: atBaseline,
        cart,
        reason: atBaseline ? 'nothing_to_restore' : 'restore_incomplete',
        classified,
      };
    }

    const cartPath = (window.FoxTheme?.routes?.cart_url || '/cart').replace(/\.js$/, '');
    const response = await fetch(`${cartPath}/update.js`, {
      ...this.cartFetchConfig('javascript'),
      body: JSON.stringify({
        updates,
        sections: sectionsToBundle,
        sections_url: window.location.pathname,
      }),
    });

    let parsedState = null;
    try {
      parsedState = await response.json();
    } catch (_) {
      parsedState = null;
    }

    const fresh =
      parsedState && Array.isArray(parsedState.items) ? parsedState : await this.fetchCart();

    if (parsedState?.sections) {
      fresh.sections = parsedState.sections;
    }
    await this.ensureCartSections(fresh, sectionsToBundle);
    this.publishCartLifecycle(fresh);

    if (!response.ok) {
      return { ok: false, cart: fresh, reason: 'update_failed', classified };
    }

    // Floor guard + exact baseline verification
    for (const id of targetedIds) {
      const floor = beforeSnapshot.totals.get(id) || 0;
      const actual = this.countPlainVariantQuantity(fresh, id);
      if (actual < floor) {
        this.log('restore undershoot', { id, floor, actual });
        return { ok: false, cart: fresh, reason: 'restore_undershoot', classified };
      }
    }

    if (!this.isAtBaseline(beforeSnapshot, fresh, expectedDelta)) {
      this.log('restore incomplete', {
        before: Object.fromEntries(beforeSnapshot.totals),
        after: Object.fromEntries(
          [...expectedDelta.keys()].map((id) => [id, this.countPlainVariantQuantity(fresh, id)])
        ),
      });
      return { ok: false, cart: fresh, reason: 'restore_incomplete', classified };
    }

    return { ok: true, cart: fresh, reason: 'restored', classified };
  }

  shopifyAddErrorMessage(parsedState) {
    return (
      parsedState?.description ||
      parsedState?.message ||
      this.inventoryUnavailableLabel ||
      'Konnte nicht hinzugefügt werden.'
    );
  }

  /**
   * Shopify 422 / failed ATC: show Shopify's inventory copy, fetch the
   * authoritative cart, recalculate cart-aware cards, keep the current
   * flavor selection. Never retry ATC. Never clear cart or cookies.
   */
  async resyncAfterFailedAdd(errorMessage, context = {}) {
    const preservedVariantId = this.currentVariant?.id;
    const { beforeSnapshot, expectedDelta, sectionsToBundle } = context;
    try {
      if (beforeSnapshot && expectedDelta && expectedDelta.size > 0) {
        await this.reconcileFailedAdd(
          beforeSnapshot,
          expectedDelta,
          sectionsToBundle || [],
          errorMessage
        );
      } else {
        const cartJson = await this.fetchCart();
        await this.ensureCartSections(cartJson, sectionsToBundle || []);
        this.publishCartLifecycle(cartJson);
        this.showError(errorMessage);
      }
    } catch (resyncError) {
      this.log('resyncAfterFailedAdd', resyncError);
      try {
        const cartJson = await this.fetchCart();
        this.publishCartLifecycle(cartJson);
      } catch (_) {
        /* keep existing UI; still surface the Shopify message */
      }
      this.showError(errorMessage);
    }

    if (preservedVariantId && this.currentVariant?.id !== preservedVariantId) {
      const preserved = this.variantsById.get(String(preservedVariantId));
      if (preserved) this.handleVariantChange(preserved, { source: 'failed-add-preserve' });
    }
  }

  async handleAddToCart() {
    if (this.isSubmitting || !this.cta || this.cta.disabled) return;

    this.clearError();
    this.toggleSubmitting(true);

    let cartCommitted = false;
    let beforeSnapshot = null;
    let expectedDelta = null;
    let sectionsToBundle = [];

    try {
      let beforeCart;
      try {
        beforeCart = await this.refreshCartReservations();
      } catch (error) {
        this.log('preflight cart refresh failed', error);
        this.showError(this.inventoryUnavailableLabel);
        return;
      }

      if (!this.selectionPurchasable()) {
        this.showError(this.soldOutLabel);
        return;
      }

      const offer = this.selectedOffer;
      let items;
      try {
        items = this.buildLineItems();
      } catch (error) {
        this.showError(error.message || 'Auswahl konnte nicht vorbereitet werden.');
        return;
      }

      expectedDelta = this.expectedDeltaFromItems(items);
      beforeSnapshot = this.snapshotVariantVector(beforeCart, expectedDelta.keys());
      this.log('submit', {
        items,
        expectedDelta: Object.fromEntries(expectedDelta),
        beforeTotals: Object.fromEntries(beforeSnapshot.totals),
        isMixer: this.isMixerOffer(offer),
        isVariantMix: this.isVariantMixOffer(offer),
        usesAllocator: this.usesVariantAllocator(offer),
        isQuantity: this.isQuantityOffer(offer),
      });

      sectionsToBundle = this.collectCartSections();
      const body = JSON.stringify({
        items,
        sections: sectionsToBundle,
        sections_url: window.location.pathname,
      });

      const response = await fetch(`${window.FoxTheme.routes.cart_add_url}`, {
        ...this.cartFetchConfig('javascript'),
        body,
      });
      const parsedState = await response.json();

      if (!response.ok || parsedState.status) {
        await this.resyncAfterFailedAdd(this.shopifyAddErrorMessage(parsedState), {
          beforeSnapshot,
          expectedDelta,
          sectionsToBundle,
        });
        return;
      }

      const cartJson = await this.fetchCart();
      const verified = this.verifyQuantityVector(beforeSnapshot, cartJson, expectedDelta);
      this.log('vectorVerify', verified);

      if (!verified.ok) {
        if (verified.safeToRestore) {
          const restored = await this.restoreQuantityVector(
            beforeSnapshot,
            expectedDelta,
            sectionsToBundle
          );
          this.log('shortfallRestore', restored);
          if (!restored.ok) {
            this.log('shortfallRestore incomplete', restored);
          }
        } else {
          // Ambiguous / concurrent overshoot: preserve authoritative cart, fail closed.
          await this.ensureCartSections(cartJson, sectionsToBundle);
          this.publishCartLifecycle(cartJson);
        }
        this.showError(this.inventoryUnavailableLabel);
        return;
      }

      cartCommitted = true;
      cartJson.sections = parsedState.sections;
      await this.ensureCartSections(cartJson, sectionsToBundle);

      this.publishCartLifecycle(cartJson);
      document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: parsedState } }));

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.show === 'function') {
        cartDrawer.show(this.cta);
      } else if (
        document.body.classList.contains('cart-template') ||
        window.FoxTheme?.settings?.cartType === 'page'
      ) {
        window.location.href = window.FoxTheme?.routes?.cart_url || '/cart';
        return;
      }

      this.flavorCounts.forEach((_, variantId) => this.flavorCounts.set(variantId, 0));
      this.syncFlavorUi();
      this.syncIncludedLines();
      this.updateUI();
    } catch (error) {
      console.error('[pdp-purchase-options]', error);
      if (!cartCommitted) {
        await this.resyncAfterFailedAdd('Konnte nicht hinzugefügt werden.', {
          beforeSnapshot,
          expectedDelta,
          sectionsToBundle,
        });
      } else {
        this.showError('Konnte nicht hinzugefügt werden.');
      }
    } finally {
      this.toggleSubmitting(false);
    }
  }
}

if (!customElements.get('pdp-purchase-options')) {
  customElements.define('pdp-purchase-options', PdpPurchaseOptions);
}
